import { ErrorMessage } from "@hookform/error-message";
import i18next from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { usePutCreateControl } from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { showErrorToast } from "../utils/errorToast";
import { pSBC } from "../utils/pSBC";

export const NewControlButton = ({
  refetchRiskTheme,
}: {
  refetchRiskTheme: any;
}) => {
  const [editControl, setEditControl] = useState<boolean>(false);

  const { id } = useParams(); //pegar o id do risco para salvar o parametro
  const { designSystem } = useDesignSystem();

  const clickModal = () => {
    setEditControl(!editControl);
  };

  // Função para impedir a propagação do clique
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<any>({
    defaultValues: {
      initial_date: `${new Date().getFullYear()}-${(
        "0" +
        (new Date().getMonth() + 1)
      ).slice(-2)}-${("0" + new Date().getDate()).slice(-2)}`, // today date
      going: 0,
      progressPerc: 0,
    },
  });

  const { mutate: createControl } = usePutCreateControl();

  const onSubmit = (data: any) => {
    const results = {
      //dados do controle
      name: data.name,
      plannedStartDate: String(data.initial_date), //.split('-').reverse().join('/'),
      actualStartDate: data.actualStartDate
        ? String(data.actualStartDate)
        : null,
      plannedFinishDate: String(data.term), //.split('-').reverse().join('/'),
      actualFinishDate: data.final_date ? String(data.final_date) : null, //.split('-').reverse().join('/') : null,
      progressPerc: data.going,
      globalGoal: data.globalGoal,
    };

    createControl(
      { results, risk_id: id },
      {
        onSuccess: () => {
          refetchRiskTheme();
          reset();
          setEditControl(false);
          toast.success("Controle adicionado com sucesso");
        },
        onError: (error) => {
          showErrorToast(error, "Erro: Controle não adicionado");
        },
      }
    );
  };

  const initialDate = watch("initial_date");
  const progressPerc = watch("going");

  const itemsCss = "flex items-center gap-2 text-sm";
  const namesCss =
    "font-openSans font-bold text-[13px] leading-[16px] tracking-[0.18px] min-w-[6rem]";
  return (
    <>
      <button
        className='p-1 mb-1 mt-2 px-4 py-[5px] font-openSans font-semibold text-[11px] leading-[20px] tracking-[0.18px] flex items-center border text-center rounded-md text-white'
        style={{
          backgroundColor: designSystem?.cssBasics.primaryColor,
        }}
        onClick={clickModal}
      >
        {i18next.t("button_new_control")}
      </button>

      {editControl ? (
        <div
          className='fixed inset-0 bg-black bg-opacity-15 z-50 grid justify-items-end'
          onClick={clickModal}
        >
          <div
            className='fixed top-1/2 right-1/2 bg-white border rounded-xl h-[330px] w-[650px] -translate-y-1/2 translate-x-1/2 z-60 px-5 py-4'
            onClick={stopPropagation}
          >
            <div className='font-openSans font-bold text-[16px] leading-[16px] tracking-[0.18px]' style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
              {i18next.t("add_control")}
            </div>

            <form className='p-4' onSubmit={handleSubmit(onSubmit)}>
              <div className='my-4 flex flex-col gap-1.5'>
                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>{i18next.t("name")}*:</label>
                  <input
                    className='border border-black rounded px-1 min-w-[220px]'
                    type='text'
                    {...register("name", {
                      required: "Campo necessário",
                      minLength: {
                        value: 4,
                        message: "Mínimo de 4 caracteres",
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='name'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("control_initial_date")}*:
                  </label>
                  <input
                    className='border border-black rounded px-1'
                    type='date'
                    {...register("initial_date", {
                      required: "Campo necessário",
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='initial_date'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("control_actual_initial_date")}:
                  </label>
                  <input
                    className='border border-black rounded px-1'
                    type='date'
                    {...register("actualStartDate", {
                      validate: (value: any) => {
                        const today = new Date(); // Data atual
                        const inputDate = new Date(value); // Data inserida

                        if (
                          progressPerc != 0 &&
                          progressPerc != 100 &&
                          !value
                        ) {
                          return i18next.t("need_to_start_progress");
                        }

                        if (progressPerc == 0) return true;
                        // Verificar se a data inserida é menor ou igual a hoje
                        return (
                          inputDate <= today ||
                          i18next.t("date_must_be_today_or_earlier")
                        );
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='actualStartDate'
                    render={({ message }) => (
                      <p className='text-sm'>{message}</p>
                    )}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("control_term")}*:
                  </label>
                  <input
                    className='border border-black rounded px-1'
                    type='date'
                    {...register("term", {
                      required: "Campo necessário",
                      validate: (value) => {
                        // date validation
                        if (!initialDate) return true;
                        return (
                          new Date(value) >= new Date(initialDate) ||
                          "Prazo deve ser depois da data de início"
                        );
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='term'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("control_final_date")}:
                  </label>
                  <input
                    className='border border-black rounded px-1'
                    type='date'
                    {...register("final_date", {
                      validate: (valueFinalDate) => {
                        // date validation
                        if (!valueFinalDate) return true;
                        if (
                          initialDate &&
                          new Date(valueFinalDate) < new Date(initialDate)
                        ) {
                          return `Conclusão deve ser depois da data de início`;
                        }
                        return true;
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='final_date'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("control_going")}*:
                  </label>
                  <input
                    className='border border-black rounded px-1 min-w-[130px]'
                    type='number'
                    min={0}
                    max={100}
                    {...register("going", {
                      required: "Campo necessário",
                      validate: (value) => {
                        const finished = watch("final_date");
                        const actualStartDate = watch("actualStartDate");
                        if (finished && parseInt(value) !== 100) {
                          return i18next.t(
                            "progress_must_be_100_when_finished"
                          );
                        }
                        if (actualStartDate && value == 0) {
                          return i18next.t(
                            "if_started_the_percentage_cannot_be_zero"
                          );
                        }
                        if (value == 100 && !(finished && actualStartDate)) {
                          return i18next.t("need_start_and_finished_date");
                        }
                        return true;
                      },
                    })}
                  ></input>
                  <ErrorMessage
                    errors={errors}
                    name='going'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                <div className={itemsCss}>
                  <label className={namesCss} style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>{"OKR"}*:</label>
                  <select
                    className={`border border-black rounded px-1 min-w-[130px] min-h-[27.71px]`}
                    {...register("globalGoal", {
                      required: i18next.t("required_field"),
                    })}
                  >
                    <option value='true'>Sim</option>
                    <option value='false'>Não</option>
                  </select>
                  <ErrorMessage
                    errors={errors}
                    name='globalGoal'
                    render={({ message }) => <p>{message}</p>}
                  />
                </div>

                {/* <div className={`flex flex-col gap-1`}>
                                    <label className={namesCss}>Progresso:</label>
                                    <input className="border border-black rounded w-full px-1" type="text" {...register("progress")}></input>
                                    <ErrorMessage errors={errors} name="progress" render={({ message })=> <p>{message}</p>} />
                                </div> */}
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  className='h-[32px] w-[81px] border rounded-md bg-red-500 text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                  onClick={() => {
                    reset();
                    setEditControl(false);
                  }}
                >
                  {i18next.t("button_cancel")}
                </button>
                <button
                  className='h-[32px] w-[81px] border rounded-md text-white font-openSans font-bold text-sextenary text-[12px] leading-[13.98px]'
                  type='submit'
                  style={{
                    backgroundColor: designSystem?.cssBasics.primaryColor,
                  }}
                >
                  {i18next.t("button_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};
